/**
 * Quick-share modal for "Kutsu sõpru" - opens from the sidebar "Teeni tasuta krediiti"
 * link (AccountLayout). Compact share view: reward badge + how-it-works
 * steps + joined row + QR code + invite link + copy button + terms
 * link. The full view (history, balance, rules) still lives in the "Kutsu sõpru" tab
 * (ReferralSection) - `onOpenDetails` jumps there. Data comes from the same
 * `GET /api/account/referral` endpoint; loaded on first open.
 */
import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { Check, Copy, Crown, ExternalLink, MessageCircle, Zap } from "lucide-react";
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, LoadingState } from "@wa/ui";
import { useT, useI18n } from "../i18n/I18nProvider";
import { fmtMoney } from "../lib/format";
import { referral, type ReferralSummary } from "../lib/referral";

/** Public terms page per language - same map as in ReferralSection. */
const TERMS_URL: Record<"et" | "en", string> = {
  et: "https://husly.app/et/soovitusprogrammi-tingimused",
  en: "https://husly.app/en/referral-terms",
};

export function InviteModal({
  open,
  onOpenChange,
  onOpenDetails,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** Open the full view ("Kutsu sõpru" tab) - closes the modal and switches the tab. */
  onOpenDetails?: () => void;
}) {
  const t = useT();
  const { locale } = useI18n();
  const [data, setData] = useState<ReferralSummary | null>(null);
  const [failed, setFailed] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Data on first open (stays cached after the modal closes).
  useEffect(() => {
    if (!open || data) return;
    let alive = true;
    referral.summary()
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [open, data]);

  // QR code of the invite link. Always dark on white (for scannability), inside a white card.
  useEffect(() => {
    if (!data?.link) return;
    let alive = true;
    QRCode.toDataURL(data.link, { margin: 1, width: 320 })
      .then((url) => { if (alive) setQr(url); })
      .catch(() => {});
    return () => { alive = false; };
  }, [data?.link]);

  const money = (cents: number) => fmtMoney(cents, "EUR", locale);
  const termsUrl = TERMS_URL[locale === "et" ? "et" : "en"];

  function copyLink() {
    if (!data?.link) return;
    void navigator.clipboard.writeText(data.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl gap-5 p-5 sm:p-6">
        {/* ── Hero: reward badge + title + decorative gradient ──────── */}
        <div className="relative overflow-hidden rounded-xl bg-muted/60 p-6 pr-28">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-14 -top-16 h-56 w-56 opacity-80 blur-2xl"
            style={{
              background:
                "radial-gradient(circle at 30% 60%, rgba(99,102,241,.85), transparent 55%), radial-gradient(circle at 70% 35%, rgba(236,72,153,.85), transparent 55%), radial-gradient(circle at 82% 78%, rgba(249,115,22,.8), transparent 50%)",
            }}
          />
          {/* Four-pointed sparkle on top of the blob (like Lovable's hero art). */}
          <svg
            aria-hidden
            viewBox="0 0 24 24"
            className="pointer-events-none absolute right-8 top-10 size-10 text-white drop-shadow"
            fill="currentColor"
          >
            <path d="M12 0c1 7 5 11 12 12-7 1-11 5-12 12-1-7-5-11-12-12 7-1 11-5 12-12Z" />
          </svg>
          <span className="relative inline-flex rounded-full bg-background px-3 py-1 text-xs font-semibold shadow-soft">
            {data ? t("inviteModal.badge", { amount: money(data.rules.rewardCents) }) : t("inviteModal.badgeGeneric")}
          </span>
          <DialogTitle className="relative mt-3 text-2xl font-extrabold tracking-tight sm:text-3xl">
            {t("inviteModal.title")}
          </DialogTitle>
          <DialogDescription className="relative mt-1 text-base font-medium">
            {t("inviteModal.subtitle")}
          </DialogDescription>
        </div>

        {failed ? (
          <p className="py-6 text-center text-sm text-muted-foreground">{t("referral.error")}</p>
        ) : !data ? (
          <LoadingState label={t("common.loading")} />
        ) : (
          <>
            {/* ── How it works ───────────────────────────────────────── */}
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">{t("inviteModal.howTitle")}</p>
              <Step icon={Zap} text={t("inviteModal.step1")} />
              {data.rules.friendRewardCents > 0 && (
                <Step icon={Crown} text={t("inviteModal.step2", { amount: money(data.rules.friendRewardCents) })} />
              )}
              <Step
                icon={MessageCircle}
                text={t("inviteModal.step3", { amount: money(data.rules.rewardCents), plan: data.rules.minPlan })}
              />
            </div>

            {/* ── Joined / credited ─────────────────────────────────────── */}
            <p className="text-sm text-muted-foreground">
              <b className="font-semibold text-foreground">{data.stats.invited}</b> {t("inviteModal.signedUp")},{" "}
              <b className="font-semibold text-foreground">{data.stats.credited}</b> {t("inviteModal.converted")}
            </p>

            {/* ── QR + link + copy ─────────────────────────────────────── */}
            <div className="flex flex-col items-stretch gap-3 rounded-xl bg-muted/60 p-3 sm:flex-row sm:items-center">
              {qr && (
                <img
                  src={qr}
                  alt=""
                  className="mx-auto size-28 shrink-0 rounded-lg bg-white p-1.5 shadow-soft sm:mx-0"
                />
              )}
              <div className="min-w-0 flex-1 space-y-2">
                <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2.5">
                  <ExternalLink className="size-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm font-medium">{data.link}</span>
                </div>
                <Button className="w-full" onClick={copyLink}>
                  {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                  {copied ? t("referral.copied") : t("referral.copyLink")}
                </Button>
              </div>
            </div>

            {/* ── Terms + full view ──────────────────────────────────── */}
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-sm">
              <a href={termsUrl} target="_blank" rel="noreferrer" className="font-medium text-muted-foreground hover:text-foreground hover:underline">
                {t("inviteModal.terms")}
              </a>
              {onOpenDetails && (
                <button
                  type="button"
                  onClick={() => { onOpenChange(false); onOpenDetails(); }}
                  className="font-medium text-primary hover:underline"
                >
                  {t("inviteModal.details")}
                </button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** One "how it works" row - icon + text. */
function Step({ icon: Icon, text }: { icon: typeof Zap; text: string }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 size-4.5 shrink-0 text-foreground" />
      <p className="text-sm text-foreground">{text}</p>
    </div>
  );
}
